import * as React from "react";
import {
  IUser,
  IUserFilter,
  IUsersAnalytics,
  OnUpdateFilterLabelAndCondition,
  UserFilter,
} from "../../../../../ts/App/Analytics/Common";
import { AnalyticsViews } from "../../../../../ts/App/Analytics/Common/index";
import {
  ClearAllCross,
  ClearAllText,
  ClearFiltersButton,
  ClickableCross,
  CollapseArrowDown,
  CollapseArrowUp,
  FilterActionsContainer,
  FilterContainer,
  FilterCriteriaCard,
  FilterCriteriaHeader,
  FilterExportIconsContainer,
  FiltersContainer,
  Icon,
} from "./FilterCriteria.styles";
import { comparators } from "../UsersTablePage/UsersPage";
import {
  ExportFilteredCSVIcon,
  ExportRawCSVIcon,
  ExportXLSXIcon,
  PrintPDFIcon,
} from "../../../utils/Icons/Analytics/FilterCriteriaIcons";
import ToolTip from "react-tooltip";
import colors from "../../../../styles/colors";
import { getComponentForDisplay } from "./UserFilters";
import { CustomCheckBox } from "../UsersTablePage/UsersPage.styles";
import { DragSourceContainer } from "../../CourseAuthor/CourseInstruction/Parts/ControlBar.styles";

export interface IFilterCriteriaOwnProps {
  filters: IUserFilter[];
  onRemoveFilter: (name: string, type: UserFilter) => () => void;
  onRemoveLabelAndCondition: OnUpdateFilterLabelAndCondition;
  onClearAllFilters: () => void;
  onPrint: (e: any) => void;
  selectedCount: number;
  allUsersCount: number;
  users: IUser[];
  usersAnalytics: IUsersAnalytics;
  selectedFromGraph: Set<string>;
  selected?: Set<string>;
  view: AnalyticsViews;
  showFilteredTable: boolean;
  toggleShowFilteredTable: () => void;
}

interface IFilterCriteriaDispatchProps {
  onExport: (users: IUser[], usersAnalytics: IUsersAnalytics, isXLSX: boolean) => void;
}

interface FilterCardHeaderProps {
  filters: IUserFilter[];
  onClearAllFilters: () => void;
  isCollapsed: boolean;
  handleCollapse: () => void;
  selectedCount: number;
  allUsersCount: number;
  onPrint: (e: any) => void;
  handleExportFilteredCSV: () => void;
  handleExportRawCSV: () => void;
  handleExportXLSX: () => void;
}

interface FilterCountProps {
  selectedCount: number;
  allUsersCount: number;
  inFilterCard: boolean;
}

interface ITooltipWrapperProps {
  id: string;
  content: string;
  children: React.ReactNode;
  containerStyle?: React.CSSProperties;
}

interface CollapseArrowProps {
  isCollapsed: boolean;
  handleCollapse: () => void;
}

interface FilterCardListProps {
  filters: IUserFilter[];
  onRemoveFilter: (name: string, type: UserFilter) => () => void;
  onRemoveLabelAndCondition: OnUpdateFilterLabelAndCondition;
}

type IFilterCriteriaProps = IFilterCriteriaOwnProps & IFilterCriteriaDispatchProps;

export const FilterCriteria: React.FC<IFilterCriteriaProps> = ({
  filters,
  onRemoveFilter,
  onClearAllFilters,
  onRemoveLabelAndCondition,
  onPrint,
  selectedCount,
  allUsersCount,
  onExport,
  users,
  usersAnalytics,
  selectedFromGraph,
  selected,
  view,
  showFilteredTable,
  toggleShowFilteredTable,
}) => {
  const [ordering] = React.useState(["learnerName", "asc"] as [string, string]);

  const filteredUsers = users;

  const selectedExists = selected !== undefined;

  const isTheFilterActive = (!selected || selected.size === 0) && (!selectedFromGraph || selectedFromGraph.size === 0);

  let sortedConditionallyFilteredUsers = filteredUsers;

  if (selectedExists) {
    const conditionallyFilteredUsers = isTheFilterActive
      ? filteredUsers
      : filteredUsers.filter(u => selected?.has(u.accountId) || selectedFromGraph?.has(u.accountId));

    const comparator = comparators[ordering[0]];

    sortedConditionallyFilteredUsers = conditionallyFilteredUsers.sort((u1, u2) =>
      comparator(u1, u2, selected || new Set(), usersAnalytics)
    );
  }

  const handleExportFilteredCSV = () => onExport(sortedConditionallyFilteredUsers, usersAnalytics, false);

  const handleExportRawCSV = () => onExport(users, usersAnalytics, false);

  const handleExportXLSX = () => onExport(users, usersAnalytics, true);

  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const handleCollapse = () => setIsCollapsed(!isCollapsed);

  const showLearnersCheckbox = view === AnalyticsViews.Users && filters.length > 0;

  return (
    <FilterCriteriaCard className={isCollapsed ? "" : "full"}>
      <FilterCardHeader
        filters={filters}
        onClearAllFilters={onClearAllFilters}
        isCollapsed={isCollapsed}
        handleCollapse={handleCollapse}
        selectedCount={selectedCount}
        allUsersCount={allUsersCount}
        onPrint={onPrint}
        handleExportFilteredCSV={handleExportFilteredCSV}
        handleExportRawCSV={handleExportRawCSV}
        handleExportXLSX={handleExportXLSX}
      />
      <FilterCardList
        filters={filters}
        onRemoveFilter={onRemoveFilter}
        onRemoveLabelAndCondition={onRemoveLabelAndCondition}
      />
      {showLearnersCheckbox && (
        <div>
          <CustomCheckBox
            type="checkbox"
            checked={showFilteredTable}
            onChange={toggleShowFilteredTable}
            data-testid="show-learners-checkbox"
          />
          <span>Show learners who do not match filter criteria</span>
        </div>
      )}
    </FilterCriteriaCard>
  );
};

const FilterCardHeader: React.FC<FilterCardHeaderProps> = ({
  filters,
  onClearAllFilters,
  isCollapsed,
  handleCollapse,
  selectedCount,
  allUsersCount,
  onPrint,
  handleExportFilteredCSV,
  handleExportRawCSV,
  handleExportXLSX,
}) => (
  <FilterCriteriaHeader>
    <FilterActionsContainer>
      <strong>Filter Criteria</strong>
      {filters.length > 0 && (
        <ClearFiltersButton onClick={onClearAllFilters} data-testid="clear-all">
          <ClearAllText>Clear all</ClearAllText>
          <ClearAllCross />
        </ClearFiltersButton>
      )}
    </FilterActionsContainer>
    <FilterCount selectedCount={selectedCount} allUsersCount={allUsersCount} inFilterCard={true} />
    <FilterExportIconsContainer data-testid="filter-export-icons-container">
      <TooltipWrapper id="export-filtered-csv" content="Export sorted and filtered CSV">
        <ExportFilteredCSVIcon onClick={handleExportFilteredCSV} />
      </TooltipWrapper>
      <TooltipWrapper id="export-raw-csv" content="Export CSV">
        <ExportRawCSVIcon onClick={handleExportRawCSV} />
      </TooltipWrapper>
      <TooltipWrapper id="export-xlsx" content="Export xlsx">
        <ExportXLSXIcon onClick={handleExportXLSX} disabled={false} />
      </TooltipWrapper>
      <TooltipWrapper id="export-pdf" content="Print">
        <PrintPDFIcon onClick={onPrint} />
      </TooltipWrapper>
      {filters.length > 0 && <CollapseArrow isCollapsed={isCollapsed} handleCollapse={handleCollapse} />}
    </FilterExportIconsContainer>
  </FilterCriteriaHeader>
);

export const FilterCount: React.FC<FilterCountProps> = ({ selectedCount, allUsersCount, inFilterCard }) => {
  return (
    <div>
      {selectedCount === 0 && inFilterCard ? (
        <>
          <strong>
            <Icon className="icon-filter" />
          </strong>
          {allUsersCount}
        </>
      ) : (
        <div data-testid="selected-filter-count">
          <strong>
            <Icon className="icon-filter" />
            {selectedCount}{" "}
          </strong>
          / {allUsersCount}
        </div>
      )}
    </div>
  );
};

export const TooltipWrapper: React.FC<ITooltipWrapperProps> = ({ id, content, children, containerStyle }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  const handleShowTooltip = () => setShowTooltip(true);
  const handleHideTooltip = () => setShowTooltip(false);

  return (
    <>
      <DragSourceContainer
        data-for={id}
        data-tip=""
        onMouseEnter={handleShowTooltip}
        onMouseLeave={handleHideTooltip}
        onMouseDown={handleHideTooltip}
        style={containerStyle}
      >
        {children}
      </DragSourceContainer>
      {showTooltip && (
        <ToolTip place="top" id={id} backgroundColor={colors.darkblue}>
          <strong>{content}</strong>
        </ToolTip>
      )}
    </>
  );
};

const CollapseArrow: React.FC<CollapseArrowProps> = ({ isCollapsed, handleCollapse }) => (
  <div data-testid="collapse-arrow">
    {isCollapsed ? <CollapseArrowDown onClick={handleCollapse} /> : <CollapseArrowUp onClick={handleCollapse} />}
  </div>
);

const FilterCardList: React.FC<FilterCardListProps> = ({ filters, onRemoveFilter, onRemoveLabelAndCondition }) => (
  <FiltersContainer>
    {filters.map((f, i) => (
      <FilterContainer key={i}>
        {getComponentForDisplay(f.name, f.type, f.labels, onRemoveLabelAndCondition(f.name, f.type))}
        <ClickableCross onClick={onRemoveFilter(f.name, f.type)} data-testid="clickable-cross" />
      </FilterContainer>
    ))}
  </FiltersContainer>
);
